import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getMSGraphScopeAndEndPoint } from '../shared/utils';
import { MSGraphScopes } from '../shared/constants';
import { useAuth } from '@iamexperiences/feature-auth';
import { getMePhotoUrl, getProfilePhotoAllowedOperationsUrl } from '../redux/modules/graphApis';
import { useTelemetry } from '@iamexperiences/ecos-telemetry';

interface IProfilePhotoAllowedOperations {
  allowedOperations: string;
}

export const useGetProfilePhotoAllowedOperations = () => {
  const { auth } = useAuth();
  const telemetry = useTelemetry();
  return useQuery({
    queryKey: ['getPhotoAllowedOperations'],
    staleTime: Infinity,
    queryFn: async () => {
      const token = await auth.acquireToken({
        scopes: [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll).scope],
      });
      const response = await fetch(getProfilePhotoAllowedOperationsUrl(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.text();
        telemetry.error('update-photo/getAllowedOperations', error);
        throw new Error(`Failed to get ProfilePhotoAllowedOperations: ${error}`);
      }
      const json = await response.json();
      return (json as IProfilePhotoAllowedOperations).allowedOperations;
    },
  });
};

export const useUpdatePhoto = () => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async (blob: Blob) => {
      const token = await auth.acquireToken({
        scopes: [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope],
      });

      const response = await fetch(getMePhotoUrl(), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': blob.type,
        },
        body: blob,
      });

      if (!response.ok) {
        throw new Error(`Failed to UpdatePhoto: ${await response.text()}`);
      }

      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('getUserPhoto');
        queryClient.invalidateQueries('getPhotoAllowedOperations');
      },
    },
  );
};

export const useRemovePhoto = () => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      const token = await auth.acquireToken({
        scopes: [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope],
      });

      const response = await fetch(getMePhotoUrl(), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to DeletePhoto: ${await response.text()}`);
      }

      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('getUserPhoto');
        queryClient.invalidateQueries('getPhotoAllowedOperations');
      },
    },
  );
};
